import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Users, 
  Ruler, 
  FileCheck, 
  HelpCircle,
  CheckCircle2,
  Lightbulb,
  Calculator,
  ClipboardList,
  Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn, StaggerContainer, StaggerItem } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const supportIcons = [Lightbulb, HelpCircle, Ruler, FileCheck, Calculator, ClipboardList, Handshake, Users];

export default function Consultants() {
  const { language } = useLanguage();

  const supportAreas = {
    en: [
      { title: 'Concept Definition', description: 'We help you develop compelling fountain concepts that align with project objectives and client expectations.' },
      { title: 'Technical Feasibility', description: 'Assessment of site conditions, hydraulic requirements, and technical constraints for fountain installations.' },
      { title: 'Hydraulic Understanding', description: 'Expert guidance on water behavior, pump sizing, nozzle selection, and system performance calculations.' },
      { title: 'System Design Support', description: 'Detailed technical input for fountain system design, including drawings and specifications.' },
      { title: 'BOQ Development', description: 'Comprehensive Bill of Quantities for fountain works, ensuring accurate project costing.' },
      { title: 'Tender Specs Support', description: 'Professional assistance in drafting technical specifications for fountain tender documents.' },
      { title: 'Execution Coordination', description: 'Technical oversight and coordination support during the construction and installation phases.' },
      { title: 'Client Presentations', description: 'Support for client-facing presentations with 3D visualizations and technical explanations.' },
    ],
    fr: [
      { title: 'Définition du Concept', description: 'Nous vous aidons à développer des concepts de fontaines convaincants alignés avec les objectifs du projet et attentes du client.' },
      { title: 'Faisabilité Technique', description: 'Évaluation des conditions du site, exigences hydrauliques et contraintes techniques pour les installations de fontaines.' },
      { title: 'Compréhension Hydraulique', description: 'Accompagnement expert sur le comportement de l\'eau, dimensionnement des pompes, sélection des ajutages et calculs de performance.' },
      { title: 'Support Design Système', description: 'Apport technique détaillé pour le design du système de fontaine, incluant dessins et spécifications.' },
      { title: 'Développement BOQ', description: 'Bordereau de Quantités complet pour les travaux de fontaines, assurant un chiffrage précis du projet.' },
      { title: 'Support Cahier des Charges', description: 'Assistance professionnelle pour la rédaction des spécifications techniques des documents d\'appel d\'offres fontaines.' },
      { title: 'Coordination d\'Exécution', description: 'Supervision technique et support de coordination pendant les phases de construction et installation.' },
      { title: 'Présentations Client', description: 'Support pour les présentations client avec visualisations 3D et explications techniques.' },
    ],
    es: [
      { title: 'Definición del Concepto', description: 'Le ayudamos a desarrollar conceptos de fuentes convincentes alineados con los objetivos del proyecto y expectativas del cliente.' },
      { title: 'Viabilidad Técnica', description: 'Evaluación de condiciones del sitio, requisitos hidráulicos y restricciones técnicas para instalaciones de fuentes.' },
      { title: 'Comprensión Hidráulica', description: 'Orientación experta sobre comportamiento del agua, dimensionamiento de bombas, selección de boquillas y cálculos de rendimiento.' },
      { title: 'Soporte de Diseño de Sistema', description: 'Aporte técnico detallado para el diseño del sistema de fuente, incluyendo dibujos y especificaciones.' },
      { title: 'Desarrollo de BOQ', description: 'Presupuesto de Cantidades completo para trabajos de fuentes, asegurando un costeo preciso del proyecto.' },
      { title: 'Soporte de Especificaciones', description: 'Asistencia profesional para redactar especificaciones técnicas de documentos de licitación de fuentes.' },
      { title: 'Coordinación de Ejecución', description: 'Supervisión técnica y soporte de coordinación durante las fases de construcción e instalación.' },
      { title: 'Presentaciones al Cliente', description: 'Soporte para presentaciones al cliente con visualizaciones 3D y explicaciones técnicas.' },
    ],
  };

  const whoWeSupport = {
    en: [
      { title: 'Engineering Offices (BET)', description: 'Technical bureaus requiring fountain system expertise for their project documentation and coordination.' },
      { title: 'Architects & Designers', description: 'Design professionals seeking to integrate water features into their architectural visions.' },
      { title: 'Landscape Architects', description: 'Specialists incorporating fountains into outdoor spaces and public realm projects.' },
      { title: 'Project Consultants', description: 'Consulting firms managing complex projects with fountain components.' },
      { title: 'Project Owners & Developers', description: 'Direct engagement with owners requiring independent technical guidance.' },
      { title: 'General Contractors', description: 'Construction companies seeking specialized fountain installation support.' },
    ],
    fr: [
      { title: 'Bureaux d\'Études (BET)', description: 'Bureaux techniques nécessitant une expertise en systèmes de fontaines pour leur documentation et coordination de projet.' },
      { title: 'Architectes & Designers', description: 'Professionnels du design cherchant à intégrer des jeux d\'eau dans leurs visions architecturales.' },
      { title: 'Architectes Paysagistes', description: 'Spécialistes intégrant des fontaines dans les espaces extérieurs et projets d\'espace public.' },
      { title: 'Consultants de Projet', description: 'Cabinets de conseil gérant des projets complexes avec des composants fontaines.' },
      { title: 'Maîtres d\'Ouvrage & Promoteurs', description: 'Engagement direct avec les propriétaires nécessitant un accompagnement technique indépendant.' },
      { title: 'Entreprises Générales', description: 'Entreprises de construction recherchant un support spécialisé pour l\'installation de fontaines.' },
    ],
    es: [
      { title: 'Oficinas de Ingeniería (BET)', description: 'Oficinas técnicas que requieren experiencia en sistemas de fuentes para su documentación y coordinación de proyectos.' },
      { title: 'Arquitectos & Diseñadores', description: 'Profesionales del diseño que buscan integrar fuentes en sus visiones arquitectónicas.' },
      { title: 'Arquitectos Paisajistas', description: 'Especialistas que incorporan fuentes en espacios exteriores y proyectos de espacio público.' },
      { title: 'Consultores de Proyecto', description: 'Firmas consultoras que gestionan proyectos complejos con componentes de fuentes.' },
      { title: 'Propietarios & Promotores', description: 'Compromiso directo con propietarios que requieren orientación técnica independiente.' },
      { title: 'Contratistas Generales', description: 'Empresas de construcción que buscan soporte especializado para instalación de fuentes.' },
    ],
  };

  const currentSupportAreas = supportAreas[language] || supportAreas.en;
  const currentWhoWeSupport = whoWeSupport[language] || whoWeSupport.en;

  const pageText = {
    en: {
      eyebrow: 'For Consultants & Architects',
      title: 'Technical Assistance for Design Professionals',
      subtitle: 'Partner with Magnum Fountain to enhance your project capabilities with specialized fountain engineering expertise. We support you at every stage of the design and execution process.',
      valueEyebrow: 'Your Technical Partner',
      valueTitle: 'Fountain Expertise On Demand',
      valueDesc: "Fountain systems require specialized knowledge that most design firms don't have in-house. We bridge that gap, providing the technical depth you need while you maintain control of your project.",
      valuePoints: [
        'No need to hire full-time fountain specialists',
        'Access to European technology and standards',
        'Reduced project risk through expert validation',
        'Enhanced client confidence in your deliverables',
        'Seamless integration with your workflow',
      ],
      howWeSupport: 'How We Support You',
      howWeSupportTitle: 'Comprehensive Technical Assistance',
      howWeSupportDesc: 'From initial concept to execution coordination, we provide specialized support at every stage of your fountain project.',
      whoTitle: 'Who We Work With',
      whoSubtitle: 'Design & Engineering Professionals',
      engagementEyebrow: 'Engagement Models',
      engagementTitle: 'Flexible Collaboration Options',
      engagementDesc: 'We adapt our support to your specific needs and project requirements.',
      models: [
        { num: '01', title: 'Project-Based', desc: 'Dedicated support for a specific project, from concept through completion. Clear scope and deliverables defined upfront.' },
        { num: '02', title: 'Consultation Hours', desc: 'Flexible technical consultation time for guidance, validation, or problem-solving across multiple projects.' },
        { num: '03', title: 'Retainer Partnership', desc: 'Ongoing technical partnership with priority access to our expertise for firms with regular fountain projects.' },
      ],
      confidentialityTitle: 'Confidentiality & Professionalism',
      confidentialityDesc: 'We understand the sensitive nature of design projects. All our engagements are governed by strict confidentiality agreements. We work as an extension of your team, supporting your success without competing for your clients.',
      confidentialityTags: ['NDA Available', 'White-Label Support', 'Your Client Relationship'],
      ctaTitle: "Let's Discuss Your Project Needs",
      ctaDesc: "Whether you're facing a specific technical challenge or need comprehensive support for an upcoming fountain project, we're here to help.",
      ctaBtn: 'Schedule a Technical Call',
      ctaBtn2: 'Email Us Directly',
    },
    fr: {
      eyebrow: 'Pour Consultants & Architectes',
      title: 'Assistance Technique pour Professionnels du Design',
      subtitle: 'Partenariat avec Magnum Fountain pour améliorer les capacités de votre projet avec une expertise spécialisée en ingénierie de fontaines. Nous vous soutenons à chaque étape du processus de design et d\'exécution.',
      valueEyebrow: 'Votre Partenaire Technique',
      valueTitle: 'Expertise Fontaines à la Demande',
      valueDesc: 'Les systèmes de fontaines nécessitent des connaissances spécialisées que la plupart des bureaux de design n\'ont pas en interne. Nous comblons ce fossé, fournissant la profondeur technique dont vous avez besoin tout en maintenant le contrôle de votre projet.',
      valuePoints: [
        'Pas besoin d\'embaucher des spécialistes fontaines à temps plein',
        'Accès à la technologie et aux standards européens',
        'Risque projet réduit grâce à la validation experte',
        'Confiance client accrue dans vos livrables',
        'Intégration transparente avec votre workflow',
      ],
      howWeSupport: 'Comment Nous Vous Soutenons',
      howWeSupportTitle: 'Assistance Technique Complète',
      howWeSupportDesc: 'Du concept initial à la coordination d\'exécution, nous fournissons un support spécialisé à chaque étape de votre projet de fontaine.',
      whoTitle: 'Avec Qui Nous Travaillons',
      whoSubtitle: 'Professionnels du Design & de l\'Ingénierie',
      engagementEyebrow: 'Modèles d\'Engagement',
      engagementTitle: 'Options de Collaboration Flexibles',
      engagementDesc: 'Nous adaptons notre support à vos besoins spécifiques et exigences de projet.',
      models: [
        { num: '01', title: 'Par Projet', desc: 'Support dédié pour un projet spécifique, du concept à l\'achèvement. Périmètre et livrables clairs définis dès le départ.' },
        { num: '02', title: 'Heures de Consultation', desc: 'Temps de consultation technique flexible pour l\'accompagnement, validation ou résolution de problèmes sur plusieurs projets.' },
        { num: '03', title: 'Partenariat Retainer', desc: 'Partenariat technique continu avec accès prioritaire à notre expertise pour les bureaux avec des projets fontaines réguliers.' },
      ],
      confidentialityTitle: 'Confidentialité & Professionnalisme',
      confidentialityDesc: 'Nous comprenons la nature sensible des projets de design. Tous nos engagements sont régis par des accords de confidentialité stricts. Nous travaillons comme une extension de votre équipe, soutenant votre succès sans concurrencer pour vos clients.',
      confidentialityTags: ['NDA Disponible', 'Support Marque Blanche', 'Votre Relation Client'],
      ctaTitle: 'Discutons de Vos Besoins de Projet',
      ctaDesc: 'Que vous fassiez face à un défi technique spécifique ou ayez besoin d\'un support complet pour un projet de fontaine à venir, nous sommes là pour vous aider.',
      ctaBtn: 'Planifier un Appel Technique',
      ctaBtn2: 'Nous Contacter par Email',
    },
    es: {
      eyebrow: 'Para Consultores y Arquitectos',
      title: 'Asistencia Técnica para Profesionales del Diseño',
      subtitle: 'Asóciese con Magnum Fountain para mejorar las capacidades de su proyecto con experiencia especializada en ingeniería de fuentes. Le apoyamos en cada etapa del proceso de diseño y ejecución.',
      valueEyebrow: 'Su Socio Técnico',
      valueTitle: 'Experiencia en Fuentes a Demanda',
      valueDesc: 'Los sistemas de fuentes requieren conocimientos especializados que la mayoría de las oficinas de diseño no tienen internamente. Cerramos esa brecha, proporcionando la profundidad técnica que necesita mientras mantiene el control de su proyecto.',
      valuePoints: [
        'No necesita contratar especialistas en fuentes a tiempo completo',
        'Acceso a tecnología y estándares europeos',
        'Riesgo de proyecto reducido a través de validación experta',
        'Mayor confianza del cliente en sus entregables',
        'Integración perfecta con su flujo de trabajo',
      ],
      howWeSupport: 'Cómo Le Apoyamos',
      howWeSupportTitle: 'Asistencia Técnica Integral',
      howWeSupportDesc: 'Desde el concepto inicial hasta la coordinación de ejecución, proporcionamos soporte especializado en cada etapa de su proyecto de fuente.',
      whoTitle: 'Con Quién Trabajamos',
      whoSubtitle: 'Profesionales de Diseño e Ingeniería',
      engagementEyebrow: 'Modelos de Compromiso',
      engagementTitle: 'Opciones de Colaboración Flexibles',
      engagementDesc: 'Adaptamos nuestro soporte a sus necesidades específicas y requisitos del proyecto.',
      models: [
        { num: '01', title: 'Por Proyecto', desc: 'Soporte dedicado para un proyecto específico, desde el concepto hasta la finalización. Alcance y entregables claros definidos desde el inicio.' },
        { num: '02', title: 'Horas de Consulta', desc: 'Tiempo de consulta técnica flexible para orientación, validación o resolución de problemas en múltiples proyectos.' },
        { num: '03', title: 'Asociación Retainer', desc: 'Asociación técnica continua con acceso prioritario a nuestra experiencia para firmas con proyectos de fuentes regulares.' },
      ],
      confidentialityTitle: 'Confidencialidad y Profesionalismo',
      confidentialityDesc: 'Entendemos la naturaleza sensible de los proyectos de diseño. Todos nuestros compromisos están regidos por acuerdos de confidencialidad estrictos. Trabajamos como una extensión de su equipo, apoyando su éxito sin competir por sus clientes.',
      confidentialityTags: ['NDA Disponible', 'Soporte Marca Blanca', 'Su Relación con el Cliente'],
      ctaTitle: 'Hablemos de Sus Necesidades de Proyecto',
      ctaDesc: 'Ya sea que enfrente un desafío técnico específico o necesite soporte integral para un próximo proyecto de fuente, estamos aquí para ayudar.',
      ctaBtn: 'Programar una Llamada Técnica',
      ctaBtn2: 'Envíenos un Email Directamente',
    },
  };

  const text = pageText[language] || pageText.en;

  return (
    <main data-testid="consultants-page">
      <SEO page="consultants" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {text.eyebrow}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {text.title}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {text.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <Section background="white">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={text.valueEyebrow}
              title={text.valueTitle}
              description={text.valueDesc}
            />
            <div className="space-y-4 mt-8">
              {text.valuePoints.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#29ABE2] flex-shrink-0" />
                  <span className="text-[#6C757D]">{item}</span>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <img
              src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3Mzg1NjMwMHww&ixlib=rb-4.1.0&q=85"
              alt="Professional consultation"
              className="w-full aspect-square object-cover rounded-sm"
            />
          </FadeIn>
        </div>
      </Section>

      {/* Support Areas Grid */}
      <Section background="surface">
        <SectionTitle
          eyebrow={text.howWeSupport}
          title={text.howWeSupportTitle}
          description={text.howWeSupportDesc}
          align="center"
        />
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {currentSupportAreas.map((area, index) => {
            const Icon = supportIcons[index] || Lightbulb;
            return (
              <StaggerItem key={index}>
                <div className="bg-white border border-gray-100 p-6 h-full hover:border-[#29ABE2]/30 transition-colors">
                  <div className="w-10 h-10 bg-[#29ABE2]/10 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#29ABE2]" />
                  </div>
                  <h3 className="text-base font-bold text-[#1A1A1A] mb-2 uppercase tracking-wide">
                    {area.title}
                  </h3>
                  <p className="text-sm text-[#6C757D] leading-relaxed">
                    {area.description}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </Section>

      {/* Who We Support */}
      <Section background="white">
        <SectionTitle
          eyebrow={text.whoTitle}
          title={text.whoSubtitle}
          align="center"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentWhoWeSupport.map((item, index) => (
            <FadeIn key={index} delay={index * 0.1}>
              <div className="p-6 border-l-4 border-[#29ABE2] bg-[#F8F9FA]">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#6C757D]">
                  {item.description}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Engagement Models */}
      <Section background="dark">
        <SectionTitle
          eyebrow={text.engagementEyebrow}
          title={text.engagementTitle}
          description={text.engagementDesc}
          dark
          align="center"
        />
        <div className="grid md:grid-cols-3 gap-8">
          {text.models.map((model, index) => (
            <FadeIn key={index} delay={(index + 1) * 0.1}>
              <div className="bg-white/5 border border-white/10 p-8 text-center">
                <div className="text-4xl font-bold text-[#29ABE2] mb-4">{model.num}</div>
                <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">
                  {model.title}
                </h3>
                <p className="text-sm text-gray-400">
                  {model.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Confidentiality Note */}
      <Section background="surface">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-3xl font-bold text-[#1A1A1A] mb-4 uppercase tracking-wide">
              {text.confidentialityTitle}
            </h2>
            <p className="text-[#6C757D] leading-relaxed mb-6">
              {text.confidentialityDesc}
            </p>
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              {text.confidentialityTags.map((tag, index) => (
                <span key={index} className="px-4 py-2 bg-white border border-gray-200 text-[#1A1A1A]">
                  {tag}
                </span>
              ))}
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="white" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {text.ctaTitle}
            </h2>
            <p className="text-lg text-[#6C757D] mb-8">
              {text.ctaDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="consultants-cta-btn"
                  className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  {text.ctaBtn}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="mailto:info@magnumfountain.com">
                <Button
                  variant="outline"
                  className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  {text.ctaBtn2}
                </Button>
              </a>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
